import { computedFrom } from "aurelia-binding";
import { BindingSignaler } from "aurelia-templating-resources";
import * as Enumerable from "linq";
import {
    type MepEmissionTypesConfigModel, MepDeleteEmissionTypeConfigRequest, MepAbsoluteTargetConfigDetail, MepCreateOrUpdateEmissionTypeConfigRequest,
    MepEmissionTypesConfigDetailModel, type MepPortTargetYears as MepPortTargetYear
} from "../../../../../../../../models/mep/mep-emission-target-config.models";
import { MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepEmissionTargetConfigService } from "../../../../../../../../services/mep/mep-emission-target-config.service";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { useView } from "aurelia-framework";

/////////// View Model for the Emission Type 
@useView('views/pages/modules/mep/tabs/mep-emission-targets/partials/mep-emission-types-config/mep-emission-types-config.html')
export class MepEmissionTypesConfig {
    isActive: boolean;
    removeLoading: boolean = false;
    item: MepEmissionTypesConfigModel;
    baseYears: MepLookupItem[];
    isOpen: boolean;
    portId: number;
    selectedYear: MepPortTargetYear;
    hasDataLoaded: boolean;
    loadingDetails: boolean = false;
    details: MepEmissionTypesConfigVesselGroupViewModel[];
    parentVmOnRemoveItem: () => void
    formattedEmissionTypeName: string;
    selectYearForRelativeAverage: number;

    _selectedBaseYear: MepLookupItem;

    @computedFrom(Reflection.getPropertyPath<MepEmissionTypesConfig>(z => z._selectedBaseYear))
    get selectedBaseYear() {
        return this._selectedBaseYear;
    }
    set selectedBaseYear(value: MepLookupItem) {
        this._selectedBaseYear = value;
        if (this.item) {
            this.item.relativeTargetBaseYear = this._selectedBaseYear.id;
        }
        this.onSelectedBaseYearChanged();
    }

    //this is used to set the absolute value 
    isAbsoluteChanged(isChecked: boolean, eventType: string) {
        if (isChecked) {
            const isAbsoluteBefore = this.item.isAbsolute;
            switch (eventType) {
                case "absolute":
                    this.item.isAbsolute = true;
                    break;
                case "relative":
                    this.item.isAbsolute = false;
                    break;
                case "no-target":
                    this.item.isAbsolute = null;
                    break;
            }
            // if the isAbsolute changed then we need to reload the data in case that the selected relative year does not match selectedYear -1 (we use selectedYear -1 for absolute to show average relative)
            if (isAbsoluteBefore != this.item.isAbsolute && this.item.isAbsolute !== null &&
                (!this.selectedBaseYear || (this.selectedYear && this.selectedBaseYear.id != this.selectedYear.year - 1))) {
                this.loadDetails();
            }
        }
    }

    constructor(model: MepEmissionTypesConfigModel,
        portId: number,
        selectedYear: MepPortTargetYear,
        baseYears: MepLookupItem[],
        private mepEmissionTargetConfigService: MepEmissionTargetConfigService,
        private notificationClientService: NotificationClientService,
        private bindingSignaler: BindingSignaler,
        onRemoveItem: () => void) {
        this.baseYears = Enumerable.from(baseYears).where(x => x.id <= selectedYear.year).toArray();
        this.item = model;
        this._selectedBaseYear = Enumerable.from(this.baseYears).firstOrDefault(x => x.id == this.item.relativeTargetBaseYear);
        this.isActive = model.isActive;
        this.portId = portId;
        this.selectedYear = selectedYear;
        this.parentVmOnRemoveItem = onRemoveItem;
        this.formattedEmissionTypeName = this.item.htmlFormatName ? this.item.htmlFormatName : this.item.emissionTypeName;
    }

    loadDetails() {
        return new Promise<void>((resolve, reject) => {
            this.loadingDetails = true;
            //always get an relative year when not set from ui get the previous year https://rightship.atlassian.net/browse/DEL-2674
            this.selectYearForRelativeAverage = !this.item.isAbsolute && !this.selectedBaseYear || this.item.isAbsolute ? this.selectedYear.year : this.selectedBaseYear.id;
            this.mepEmissionTargetConfigService.getPortEmissionTypesConfigDetail(this.portId, this.item.emissionTypeId,
                this.selectedYear.year, this.selectYearForRelativeAverage)
                .then(result => {
                    this.loadingDetails = false;

                    if (result.hasError) {
                        this.notificationClientService.show("MEP Targets", "Error retrieving the emission target details!", NotificationType.Error);
                        this.isOpen = false;
                    }
                    else {

                        this.details = Enumerable.from(result.details).select(x => {
                            return new MepEmissionTypesConfigDetailViewModel().fromModel(x, this.item, x.weightBucketId == -1);
                        }).groupBy(x => x.vesselTypeGroupName)
                            .orderBy(y => y.key())
                            .select(x => {
                                const groupDetail = x.where(x => x.isVesselTypeGroup).firstOrDefault();
                                const detail: MepEmissionTypesConfigVesselGroupViewModel = new MepEmissionTypesConfigVesselGroupViewModel()
                                detail.error = groupDetail ? null : "Group has invalid data " + x.key();//show an error if the return value does not have a group parent i.e. one with weightbucket -1 
                                detail.isOpen = this.item.useWeightClassesInTargets;
                                detail.shouldShowChildren = this.item.useWeightClassesInTargets;
                                detail.item = groupDetail;
                                detail.children = x.where(x => x.weightBucketId != -1).orderBy(x => x.weightBucketId).toArray();
                                groupDetail.children = detail.children;
                                return detail;
                            }).toArray();
                    }
                    resolve(null);
                }).catch(() => {
                    this.loadingDetails = false;
                    this.isOpen = false;
                    this.notificationClientService.show("MEP Targets", "Error retrieving the emission target details!", NotificationType.Error);
                    reject();
                });
        });
    }

    onSelectedBaseYearChanged() {
        //here we need to re-load the data 
        this.loadDetails(); // pass true so that we change the base year
    }

    openTargetContent() {
        this.isOpen = !this.isOpen;
        if (!this.details) {
            this.loadDetails();
        }
    }

    targetYearEmissionValueChange() {
        //sending this for the binding to kick in and re compute the MepEmissionTypesConfigVesselGroupViewModel.totalizerForWeightBucket
        this.bindingSignaler.signal("weightTargetChange");
    }

    remove() {
        this.removeLoading = true;
        const requestData: MepDeleteEmissionTypeConfigRequest = {
            portId: this.portId,
            emissionTypeId: this.item.emissionTypeId,
            targetYear: this.selectedYear.year,
        };
        this.mepEmissionTargetConfigService.deleteEmissionTypeConfig(requestData).then(x => {
            this.removeLoading = false;
            if (x.hasError) {
                this.notificationClientService.show("MEP Targets", "Error removing the emission target details!", NotificationType.Error);
            }
            else {
                this.isActive = false;
                this.item.isActive = false;
                this.details = null;
                this.isOpen = false;
                if (this.parentVmOnRemoveItem)
                    this.parentVmOnRemoveItem();
                this.notificationClientService.show("MEP Targets", "Emission target details removed!", NotificationType.Info);
            }
        }).catch(() => {
            this.notificationClientService.show("MEP Targets", "Error removing the emission target details!", NotificationType.Error);
            this.removeLoading = false;
        });
    }

    save(newYear: number = null) {

        if (this.item.relativeTargetPercentage < 0 || this.item.relativeTargetPercentage > 100) {
            this.notificationClientService.show("Validation error", "Invalidate relative percentage entered!", NotificationType.Error);
            return;
        }

        if (this.item.targetThreshold < 0 || this.item.targetThreshold > 100) {
            this.notificationClientService.show("Validation error", "Invalidate target threshold percentage entered!", NotificationType.Error);
            return;
        }

        if (this.item.isAbsolute == false && !this.item.relativeTargetBaseYear) {
            this.notificationClientService.show("Validation error", "Base year for relative target must be selected!", NotificationType.Error);
            return;
        }

        const persistTargetValues: MepAbsoluteTargetConfigDetail[] = [];

        if (this.item.useWeightClassesInTargets) {
            //in such as case we should ONLY pass the data of the weight classes ignoring the vessel type groups 
            this.details.forEach(x => {
                x.children.forEach(child => {
                    persistTargetValues.push({
                        vesselTypeGroupId: child.vesselTypeGroupId,
                        weightBucketId: child.weightBucketId,
                        targetValue: child.targetYearEmissionValue
                    });
                });
            });
        } else {
            //in such as case we should ONLY pass the data of the vessel type groups ignoring the weight classes if any 
            this.details.forEach(x => {
                persistTargetValues.push({
                    vesselTypeGroupId: x.item.vesselTypeGroupId,
                    weightBucketId: null,
                    targetValue: x.item.targetYearEmissionValue
                });
            });
        }

        this.loadingDetails = true;

        const requestData: MepCreateOrUpdateEmissionTypeConfigRequest = {
            portId: this.portId,
            emissionTypeId: this.item.emissionTypeId,
            targetYear: newYear ? newYear : this.selectedYear.year,
            isAbsolute: this.item.isAbsolute,
            relativeTargetPercentageDecrease: this.item.relativeTargetPercentage,
            targetBaseYear: this.item.relativeTargetBaseYear,
            useWeightClassesInTargets: this.item.useWeightClassesInTargets,
            targetThreshold: this.item.targetThreshold,
            absoluteTargetDetails: persistTargetValues
        };

        this.mepEmissionTargetConfigService.createOrUpdateEmissionTypeConfig(requestData).then(x => {
            this.loadingDetails = false;
            if (x.hasError) {
                this.notificationClientService.show("MEP Targets", "Error saving the emission target details!", NotificationType.Error);
            }
            else {
                this.notificationClientService.show("MEP Targets", "Emission target details saved!", NotificationType.Info);
            }
        }).catch(() => {
            this.notificationClientService.show("MEP Targets", "Error saving the emission target details!", NotificationType.Error);
            this.loadingDetails = false;
        });
    }

    roundEmissionValue(value: number | null): string {
        if (value == null || value == 0) {
            return '0';
        }

        if (value >= 0.01) {
            return value.toFixed(2);
        }

        if (value >= 0.001 && value < 0.01) {
            return value.toFixed(3);
        }

        return value.toExponential(0);
    }
}

export class MepEmissionTypesConfigVesselGroupViewModel {
    error: string;
    item: MepEmissionTypesConfigDetailViewModel; // this would be the root node (weight bucket would be null here )
    children: MepEmissionTypesConfigDetailViewModel[]; // these would be the child weight buckets 
    isOpen: boolean;
    shouldShowChildren: boolean;


    @computedFrom(Reflection.getPropertyPath<MepEmissionTypesConfigVesselGroupViewModel>(z => z.children),
        Reflection.getPropertyPath<MepEmissionTypesConfigVesselGroupViewModel>(z => z.children.length))
    get totalizerForWeightBucket(): number {
        if (!this.children || !Enumerable.from(this.children).where(x => x.targetYearEmissionValue != 0).any())
            return 0;

        return Enumerable.from(this.children).where(x => x.targetYearEmissionValue != 0).sum(x => x.targetYearEmissionValue);
    }
}

export class MepEmissionTypesConfigDetailViewModel {
    vesselTypeGroupId: number;
    vesselTypeGroupName: string;
    weightBucketId: number;
    weightBucketName: string;
    baseYearEmissionValue: number;
    targetYearEmissionValue: number;
    isVesselTypeGroup: boolean;
    children: MepEmissionTypesConfigDetailViewModel[]; // these would be the child weight buckets 

    configItem: MepEmissionTypesConfigModel;

    fromModel(item: MepEmissionTypesConfigDetailModel, configItem: MepEmissionTypesConfigModel, isVesselTypeGroup: boolean): MepEmissionTypesConfigDetailViewModel {
        this.vesselTypeGroupId = item.vesselTypeGroupId;
        this.vesselTypeGroupName = item.vesselTypeGroupName;
        this.weightBucketId = item.weightBucketId;
        this.weightBucketName = item.weightBucketName;
        this.baseYearEmissionValue = item.baseYearEmissionValue;
        this.targetYearEmissionValue = item.targetYearEmissionValue;
        this.configItem = configItem;
        this.isVesselTypeGroup = isVesselTypeGroup;
        return this;
    }

    @computedFrom("baseYearEmissionValue", "configItem.relativeTargetPercentage", "configItem.useWeightClassesInTargets")
    get computedRelativeTargetValue(): number {

        // when it is a vessel group and show weight classes then we need to take sum 
        if (this.isVesselTypeGroup && this.configItem.useWeightClassesInTargets && this.children && Enumerable.from(this.children).where(x => x.computedRelativeTargetValue != 0).any()) {
            return Enumerable.from(this.children).where(x => x.computedRelativeTargetValue != 0).sum(x => x.computedRelativeTargetValue);
        }

        // Otherwise it is a weight bucket... so we need to compute percentage 
        let percentage = 0;
        if (this.configItem && this.configItem.relativeTargetPercentage != null) {
            percentage = 1 - (this.configItem.relativeTargetPercentage / 100);
        }
        const result = this.baseYearEmissionValue * percentage;
        return Math.max(0, result);
    }
}